/**
 * D8: protected projects cache signed-link PDFs in OPFS like any other project, so a
 * policy change has to reach the cache. The desktop re-checks its cached file ids with
 * `POST /project-access/files/visible` when the same person's policy version changes and
 * removes only what they can no longer read; this is the web counterpart.
 *
 * Runs before any OPFS read or write in a project namespace (`cacheOperation`). Same
 * member, new policy version: ask the server which cached ids are still readable (1,000 per
 * request) and delete the rest. A different member, no earlier record, an incomplete member
 * hint, a failed call or an inconsistent answer purges the whole namespace instead.
 */
import axios from "../../config/axios";
import { resolveApiEnvironment } from "../../native/apiEnvironment";
import { resourceAccessState } from "../../access/resourceAccess";
import { PrimeDebugLog } from "../../util/debugLog";

/** Root directory name for all file cache data in OPFS. */
export const CACHE_ROOT_DIR = "files";

const RECORD_KEY = "pp_opfs_access_policy";
const BATCH = 1000;
/** An entry name ends `_{fileId}` (see `getCacheKey` in `bytesStore.ts`): file ids are UUIDs. */
const CACHED_FILE_ID = /_([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

interface PolicyRecord {
    member: string | null;
    version: number;
    namespace: string;
}

const known = new Map<string, PolicyRecord>();
const queues = new Map<string, Promise<void>>();

const recordKey = (principalId: string, projectId: string) =>
    `${resolveApiEnvironment().origin}:${principalId}:${projectId}`;

function readRecords(): Record<string, PolicyRecord> {
    try {
        const raw = window.localStorage.getItem(RECORD_KEY);
        return raw ? (JSON.parse(raw) as Record<string, PolicyRecord>) : {};
    } catch {
        return {};
    }
}

function remember(key: string, record: PolicyRecord): void {
    known.set(key, record);
    try {
        window.localStorage.setItem(
            RECORD_KEY,
            JSON.stringify({ ...readRecords(), [key]: record }),
        );
    } catch {
        // The in-memory record still stops repeat checks in this page session.
    }
}

async function namespaceDirectory(namespace: string): Promise<FileSystemDirectoryHandle | null> {
    if (typeof navigator === "undefined" || !navigator.storage?.getDirectory) return null;
    try {
        const root = await navigator.storage.getDirectory();
        const cacheRoot = await root.getDirectoryHandle(CACHE_ROOT_DIR, { create: false });
        return await cacheRoot.getDirectoryHandle(namespace, { create: false });
    } catch (error) {
        if ((error as { name?: string })?.name === "NotFoundError") return null;
        throw error;
    }
}

/** The names of the files a namespace folder holds. */
export async function cachedEntries(directory: FileSystemDirectoryHandle): Promise<string[]> {
    const names: string[] = [];
    // @ts-ignore -- async iteration of directory handles is not in every TS DOM lib yet
    for await (const [name, handle] of directory.entries()) {
        if ((handle as FileSystemHandle).kind !== "directory") names.push(name);
    }
    return names;
}

async function purgeNamespace(namespace: string): Promise<void> {
    if (typeof navigator === "undefined" || !navigator.storage?.getDirectory) return;
    try {
        const root = await navigator.storage.getDirectory();
        const cacheRoot = await root.getDirectoryHandle(CACHE_ROOT_DIR, { create: false });
        await cacheRoot.removeEntry(namespace, { recursive: true });
    } catch (error) {
        if ((error as { name?: string })?.name !== "NotFoundError") throw error;
    }
}

/** The ids the server still lets this member read, or throws on any failure. */
async function readableIds(ids: string[], version: number): Promise<Set<string>> {
    const readable = new Set<string>();
    for (let start = 0; start < ids.length; start += BATCH) {
        const chunk = ids.slice(start, start + BATCH);
        const { data } = await axios.post<{ policy_version: number; visible: string[] }>(
            "/api/ai/project-access/files/visible",
            { ids: chunk },
            { timeout: 30_000 },
        );
        if (data?.policy_version !== version || !Array.isArray(data.visible))
            throw new Error("Project access changed during the re-check");
        const asked = new Set(chunk);
        if (data.visible.some((id) => !asked.has(id))) throw new Error("Invalid re-check response");
        data.visible.forEach((id) => readable.add(id));
    }
    return readable;
}

async function reconcile(namespace: string, principalId: string, projectId: string): Promise<void> {
    const lease = resourceAccessState();
    if (!lease?.active) return;
    if (!lease.admitted) throw new DOMException("Project access is being verified", "AbortError");
    const key = recordKey(principalId, projectId);
    const next: PolicyRecord = {
        member: lease.member_id ?? null,
        version: lease.policy_version,
        namespace,
    };
    const previous = known.get(key) ?? readRecords()[key];
    if (
        previous &&
        previous.namespace === namespace &&
        previous.member === next.member &&
        previous.version === next.version
    ) {
        known.set(key, previous);
        return;
    }
    // An unprotected project has no restricted content to hide; note the version so a
    // later activation compares against it.
    if (!lease.enforced) {
        remember(key, next);
        return;
    }

    const directory = await namespaceDirectory(namespace);
    if (directory) {
        const names = await cachedEntries(directory);
        const sameReader =
            previous !== undefined &&
            previous.namespace === namespace &&
            !!previous.member &&
            previous.member === next.member;
        let readable: Set<string> | null = null;
        if (sameReader && names.length > 0) {
            const idOf = (name: string) => CACHED_FILE_ID.exec(name)?.[1];
            const ids = Array.from(new Set(names.map(idOf).filter((id): id is string => !!id)));
            try {
                readable = ids.length ? await readableIds(ids, next.version) : new Set();
            } catch (error) {
                PrimeDebugLog("protectedCacheReconcile", "Re-check failed; purging", error);
            }
        }
        if (readable) {
            for (const name of names) {
                const id = CACHED_FILE_ID.exec(name)?.[1];
                if (id && readable.has(id)) continue;
                await directory.removeEntry(name);
            }
        } else if (names.length > 0) {
            await purgeNamespace(namespace);
        }
    }
    // Recorded only after the cache matches the new policy; a failed purge throws above
    // and the next open retries.
    remember(key, next);
}

/**
 * Brings the project's OPFS namespace in line with the admitted policy before it is read.
 * Calls for one member and project run one after another; a call that finds the cache
 * already reconciled for the current policy does nothing.
 */
export function reconcileProtectedCache(
    namespace: string,
    principalId: string,
    projectId: string,
): Promise<void> {
    const key = recordKey(principalId, projectId);
    const run = (queues.get(key) ?? Promise.resolve())
        .catch(() => undefined)
        .then(() => reconcile(namespace, principalId, projectId));
    queues.set(key, run);
    return run;
}

export function resetProtectedCacheReconcileForTests(): void {
    known.clear();
    queues.clear();
    try {
        window.localStorage.removeItem(RECORD_KEY);
    } catch {
        // ignore
    }
}
